<template>
  <div class="admin-page admin-overview">
    <section class="overview-section">
      <div class="overview-section__head">
        <div>
          <h2 class="overview-section__title">需要管理员处理</h2>
          <p class="overview-section__hint">用户提交后必须人工审核或打款的队列。有数量的请优先处理。</p>
        </div>
        <div class="overview-section__actions">
          <el-tag v-if="totalPending > 0" type="danger" effect="dark" size="large">
            待处理 {{ totalPending }}
          </el-tag>
          <el-tag v-else type="success" effect="plain" size="large">暂无待办</el-tag>
          <el-button :loading="loading" @click="loadTodos">
            <el-icon><Refresh /></el-icon>
            刷新
          </el-button>
        </div>
      </div>

      <el-alert
        v-if="loadError"
        :title="loadError"
        type="warning"
        show-icon
        class="overview-alert"
      />

      <div class="todo-grid" v-loading="loading">
        <el-card
          v-for="item in queueItems"
          :key="item.key"
          class="todo-card"
          :class="{ 'todo-card--due': item.count > 0 }"
          shadow="hover"
          @click="openItem(item)"
        >
          <div class="todo-card__row">
            <div class="todo-card__text">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </div>
            <span class="todo-count" :class="{ 'todo-count--due': item.count > 0 }">
              {{ item.count }}
            </span>
          </div>
        </el-card>
      </div>
    </section>

    <section class="overview-section">
      <div class="overview-section__head">
        <div>
          <h2 class="overview-section__title">日常运营</h2>
          <p class="overview-section__hint">没有固定待审队列，但需要管理员配置、巡查或维护。</p>
        </div>
      </div>
      <div class="ops-grid">
        <el-card
          v-for="item in opsItems"
          :key="item.path"
          class="ops-card"
          shadow="hover"
          @click="navigateTo(item.path)"
        >
          <h3>{{ item.title }}</h3>
          <p>{{ item.desc }}</p>
        </el-card>
      </div>
    </section>

    <section class="overview-section">
      <div class="overview-section__head">
        <div>
          <h2 class="overview-section__title">全部功能</h2>
          <p class="overview-section__hint">与侧栏菜单对应的管理入口。</p>
        </div>
      </div>
      <div class="management-cards">
        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/users')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><User /></el-icon></div>
            <div class="card-info">
              <h3>用户管理</h3>
              <p>查看、编辑、删除用户，调整余额</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/user-audit')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Check /></el-icon></div>
            <div class="card-info">
              <h3>用户资料审核</h3>
              <p>审核资料变更，对比原值和新值</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/referral-coupons')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Ticket /></el-icon></div>
            <div class="card-info">
              <h3>推荐券管理</h3>
              <p>管理用户礼券、专享券发放与禁用</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/referral-bindings')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Share /></el-icon></div>
            <div class="card-info">
              <h3>推荐绑定 / 分润</h3>
              <p>绑定记录、分润流水、撤销与礼遇重试</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/idcard-audit')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><CreditCard /></el-icon></div>
            <div class="card-info">
              <h3>身份证审核</h3>
              <p>核对证件照片与申请信息</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/home-carousel')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><PictureFilled /></el-icon></div>
            <div class="card-info">
              <h3>主页轮播图</h3>
              <p>管理首页轮播图的标题、图片与展示状态</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/home-news')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Bell /></el-icon></div>
            <div class="card-info">
              <h3>最新动态</h3>
              <p>管理首页「最新动态」标题、摘要、正文与封面</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/blogs')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Document /></el-icon></div>
            <div class="card-info">
              <h3>编辑推荐管理</h3>
              <p>审核、编辑、删除用户文章</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/images')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Picture /></el-icon></div>
            <div class="card-info">
              <h3>图片管理</h3>
              <p>审核用户上传头像与相册</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/knowledge/courses')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Reading /></el-icon></div>
            <div class="card-info">
              <h3>课程管理</h3>
              <p>审核知识课程、Banner 与知识点树</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/knowledge/points')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Collection /></el-icon></div>
            <div class="card-info">
              <h3>知识点管理</h3>
              <p>维护知识点、分类及前后置关系</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/knowledge/resources')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><FolderOpened /></el-icon></div>
            <div class="card-info">
              <h3>资源管理</h3>
              <p>管理知识点学习资源并绑定知识点</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/exames')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Notebook /></el-icon></div>
            <div class="card-info">
              <h3>测评管理</h3>
              <p>审核测评、官方认证、上下架</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/content-tags')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><CollectionTag /></el-icon></div>
            <div class="card-info">
              <h3>内容标签</h3>
              <p>维护测评 / 编辑推荐 / 课程字典标签</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/exam-badges')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Medal /></el-icon></div>
            <div class="card-info">
              <h3>测评徽章配置</h3>
              <p>配置金/银/铜徽章发放模式与分数线</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/exam-results')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><DataLine /></el-icon></div>
            <div class="card-info">
              <h3>测评完成记录</h3>
              <p>按时间筛选用户新完成的测评</p>
            </div>
          </div>
        </el-card>

        <el-card class="management-card" shadow="hover" @click="navigateTo('/admin/dependencies')">
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Monitor /></el-icon></div>
            <div class="card-info">
              <h3>依赖健康检查</h3>
              <p>检测数据库、Redis、邮件、微信等依赖</p>
            </div>
          </div>
        </el-card>

        <el-card
          v-if="isSuperAdmin"
          class="management-card"
          shadow="hover"
          @click="navigateTo('/admin/database-backup')"
        >
          <div class="card-content">
            <div class="card-icon"><el-icon size="48"><Download /></el-icon></div>
            <div class="card-info">
              <h3>数据库备份</h3>
              <p>下载 MySQL 全库备份（ZIP），仅超级管理员</p>
            </div>
          </div>
        </el-card>
      </div>
    </section>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { computed, onMounted, ref } from 'vue'
import {
  User,
  Document,
  Picture,
  PictureFilled,
  Bell,
  Check,
  CreditCard,
  Download,
  Notebook,
  Reading,
  Collection,
  FolderOpened,
  Ticket,
  Share,
  DataLine,
  Medal,
  Monitor,
  CollectionTag,
  Refresh
} from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import { isSuperAdmin as checkSuperAdmin } from '@/utils/sysRole'
import axios from 'axios'

const QUEUE_DEFS = [
  { key: 'idcardAudit', title: '身份证审核', desc: '核对证件照片与申请信息，通过后写入实名', path: '/admin/idcard-audit' },
  { key: 'profileAudit', title: '用户资料审核', desc: '资料变更对比通过或拒绝，拒绝须填原因', path: '/admin/user-audit' },
  { key: 'imageAudit', title: '图片审核', desc: '审核头像与相册；未通过不会出现在前台', path: '/admin/images' },
  { key: 'blogAudit', title: '编辑推荐审核', desc: '已发布但未过审的文章，通过后才进公开列表', path: '/admin/blogs' },
  { key: 'examAudit', title: '测评审核', desc: '用户自建或改稿后的测评待审', path: '/admin/exames', query: { auditFilter: '1' } },
  { key: 'courseAudit', title: '课程审核', desc: '用户自建或改内容后的课程待审', path: '/admin/knowledge/courses', query: { auditFilter: '1' } },
  { key: 'partnerApply', title: '合伙人申请', desc: '审核开通臻享资格，结果发站内信', path: '/admin/partner-applications' },
  { key: 'withdrawPending', title: '提现审核', desc: '通过或拒绝提现；拒绝须填原因并退回余额', path: '/admin/withdraw-applications', query: { status: 'pending' } },
  { key: 'withdrawPayout', title: '提现打款', desc: '已通过待导出 CSV，打款后标记已打款', path: '/admin/withdraw-applications', query: { status: 'approved' } },
  { key: 'rechargePending', title: '充值审核', desc: '待入账订单通过入账或拒绝', path: '/admin/recharge-orders' },
  { key: 'refundPending', title: '购买申诉退款', desc: '审核消费申诉，同意后全额退款', path: '/admin/purchase-refund-appeals', query: { status: 'pending' } },
  { key: 'inquiryPending', title: '意见与留言', desc: '处理意见反馈与联系我们', path: '/admin/inquiries' }
]

const OPS_ITEMS = [
  { title: '时刻巡查', desc: '屏蔽或删除不当动态与评论', path: '/admin/moments' },
  { title: '测评官方认证', desc: '在测评管理把测评标为官方认证，供资料可见性筛选', path: '/admin/exames' },
  { title: '主页轮播图', desc: '配置首页 Banner 图文与展示状态', path: '/admin/home-carousel' },
  { title: '最新动态', desc: '发布或下线首页新闻', path: '/admin/home-news' },
  { title: '内容标签', desc: '维护测评 / 文章 / 课程字典标签', path: '/admin/content-tags' },
  { title: '测评徽章配置', desc: '设置金/银/铜徽章发放规则', path: '/admin/exam-badges' },
  { title: '推荐券与绑定', desc: '发券、停用、撤销绑定、分润追回', path: '/admin/referral-coupons' },
  { title: '角色与权限', desc: '维护后台角色及接口权限', path: '/admin/roles' },
  { title: '登录 / IP 日志', desc: '查看登录记录与异常 IP', path: '/admin/user-ip-logs' },
  { title: '邮件发送日志', desc: '排查验证码与通知邮件是否发出', path: '/admin/email-logs' },
  { title: '依赖健康检查', desc: '确认数据库、Redis、邮件、支付依赖可用', path: '/admin/dependencies' }
]

export default {
  name: 'AdminDashboard',
  components: {
    User,
    Document,
    Picture,
    PictureFilled,
    Bell,
    Check,
    CreditCard,
    Download,
    Notebook,
    DataLine,
    Reading,
    Collection,
    FolderOpened,
    Ticket,
    Share,
    Medal,
    Monitor,
    CollectionTag,
    Refresh
  },
  setup() {
    const router = useRouter()
    const isSuperAdmin = ref(false)
    const loading = ref(false)
    const loadError = ref('')
    const counts = ref({})

    const checkLogin = () => {
      if (!localStorage.getItem('jwt_token')) {
        goToLogin(router)
        return false
      }
      return true
    }

    if (!checkLogin()) {
      return {}
    }

    const navigateTo = (path, query) => {
      if (query && Object.keys(query).length) {
        router.push({ path, query })
        return
      }
      router.push(path)
    }

    const openItem = (item) => {
      navigateTo(item.path, item.query)
    }

    const queueItems = computed(() => {
      const items = QUEUE_DEFS.map((item) => ({
        ...item,
        count: Number(counts.value[item.key] || 0)
      }))
      items.sort((a, b) => {
        if ((b.count > 0) !== (a.count > 0)) return b.count > 0 ? 1 : -1
        return b.count - a.count
      })
      return items
    })

    const totalPending = computed(() =>
      QUEUE_DEFS.reduce((sum, item) => sum + Number(counts.value[item.key] || 0), 0)
    )

    const loadTodos = async () => {
      loading.value = true
      loadError.value = ''
      try {
        const token = localStorage.getItem('jwt_token')
        const { data } = await axios.post('/api/v1/admin/todo/summary', {}, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        if (data?.code !== 200) {
          loadError.value = data?.message || '待办加载失败'
          counts.value = {}
          return
        }
        counts.value = data.data || {}
      } catch (e) {
        loadError.value = e.message || '待办加载失败'
        counts.value = {}
      } finally {
        loading.value = false
      }
    }

    try {
      const user = JSON.parse(localStorage.getItem('user_info') || '{}')
      isSuperAdmin.value = checkSuperAdmin(user.sysRole)
    } catch {
      isSuperAdmin.value = false
    }

    onMounted(() => {
      loadTodos()
    })

    return {
      navigateTo,
      openItem,
      isSuperAdmin,
      loading,
      loadError,
      queueItems,
      opsItems: OPS_ITEMS,
      totalPending,
      loadTodos
    }
  }
}
</script>

<style scoped>
.overview-section {
  margin-bottom: 28px;
}

.overview-section__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.overview-section__title {
  margin: 0 0 6px;
  font-size: 20px;
  color: #303133;
}

.overview-section__hint {
  margin: 0;
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}

.overview-section__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.overview-alert {
  margin-bottom: 12px;
}

.todo-grid,
.ops-grid,
.management-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.todo-card,
.ops-card,
.management-card {
  cursor: pointer;
  border: none;
  border-radius: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.todo-card:hover,
.ops-card:hover,
.management-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.todo-card--due {
  border: 1px solid #f56c6c;
}

.todo-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.todo-card__text h3,
.ops-card h3,
.card-info h3 {
  margin: 0 0 8px;
  font-size: 16px;
  color: #303133;
}

.todo-card__text p,
.ops-card p,
.card-info p {
  margin: 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
}

.todo-count {
  min-width: 40px;
  height: 40px;
  padding: 0 10px;
  border-radius: 20px;
  background: #f2f3f5;
  color: #909399;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.todo-count--due {
  background: #f56c6c;
  color: #fff;
}

.card-content {
  display: flex;
  align-items: center;
  padding: 8px 4px;
}

.card-icon {
  margin-right: 16px;
  color: #409eff;
}

@media (max-width: 768px) {
  .overview-section__head {
    flex-direction: column;
  }

  .todo-grid,
  .ops-grid,
  .management-cards {
    grid-template-columns: 1fr;
  }

  .card-content {
    flex-direction: column;
    text-align: center;
  }

  .card-icon {
    margin-right: 0;
    margin-bottom: 12px;
  }
}
</style>
